<script>
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import { getPath } from '../../utils/imageKit.ts';
  import { getStaticMapImageSrc } from '../../utils/static-map.ts';
  import PharmacySearch from '../PharmacySearch/PharmacySearch.svelte';
  import DrugSearch from '../DrugSearch/DrugSearch.svelte';
  import ProviderSearch from '../ProviderSearch/ProviderSearch.svelte';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import BaseHeading from '../BaseHeading/BaseHeading.svelte';
  import { page } from '$app/stores';

  let {
    headingTextTemplate,
    headingLevel = 'h1',
    contentTemplate,
    backgroundImage,
    mapAlt,
    mapZoom = 5,
    alignment,
    actionPromoTiles,
    searchComponent,
    dataTestId = 'masthead-search-promo',
    lat = undefined,
    lng = undefined,
    filters,
    lang,
    increaseHeadingSize = false,
    actionLinks,
  } = $props();
  
  let mdSrc = $state();
  let lgSrc = $state();
  // Only use background Image if no lat and lng exist
  if (backgroundImage && !lat && !lng) {
    // Handle Image Setup
    mdSrc = getPath(backgroundImage, { w: 404 });
    lgSrc = getPath(backgroundImage, { w: 448 }, 2);
  }

  let smallDefaultSrc = $state();
  let smallHighResSrc = $state();
  let mediumDefaultSrc = $state();
  let mediumHighResSrc = $state();
  let largeDefaultSrc = $state();
  let largeHighResSrc = $state();

  if (lat && lng) {
    // Mapbox Image Configuration
    const userAgent = $page.data && $page.data.userAgent;
    const mapboxDetails = {
      lat,
      hideMarker: true,
      zoom: mapZoom,
      theme: 'light-v10',
      userAgent
    }

    const smallOptions = {
      width: 768,
      height: 286,
      lng:
        mapZoom < 7
          ? (parseFloat(lng) - 5.5).toString()
          : mapZoom < 9
            ? (parseFloat(lng) - 0.5).toString()
            : (parseFloat(lng) - 0.025).toString(),
      ...mapboxDetails
    };

    const mediumOptions = {
      width: 1024,
      height: 480,
      lng:
        mapZoom < 7
          ? (parseFloat(lng) - 5).toString()
          : mapZoom < 9
            ? (parseFloat(lng) - 0.5).toString()
            : (parseFloat(lng) - 0.025).toString(),
      ...mapboxDetails
    };

    const largeOptions = {
      width: 1152,
      height: 480,
      lng:
        mapZoom < 7
          ? (parseFloat(lng) - 4).toString()
          : mapZoom < 9
            ? (parseFloat(lng) - 0.5).toString()
            : (parseFloat(lng) - 0.025).toString(),
      ...mapboxDetails
    };

    smallDefaultSrc = getStaticMapImageSrc(smallOptions);
    smallHighResSrc = getStaticMapImageSrc({ ...smallOptions, highRes: true });
    mediumDefaultSrc = getStaticMapImageSrc(mediumOptions);
    mediumHighResSrc = getStaticMapImageSrc({ ...mediumOptions, highRes: true });
    largeDefaultSrc = getStaticMapImageSrc(largeOptions);
    largeHighResSrc = getStaticMapImageSrc({ ...largeOptions, highRes: true });
  }
  
  const actionPromoTilesStyles =
    actionPromoTiles && actionLinks && actionLinks.length > 0
      ? `relative flex items-center justify-center text-sm md:text-0.5sm text-center w-full px-4 py-3 md:px-2 shadow-medium font-semibold rounded bg-blue-cerulean text-white  hover-hover:hover:bg-blue-darkish active:shadow-active 
      md:text-left md:text-blue-navy md:bg-white md:justify-between md:pl-4 hover-hover:md:hover:bg-gray-mercury lg:min-h-12`
      : `flex items-center relative w-full pl-4 pr-2 py-2 md:py-3 shadow-medium rounded-sm bg-white text-blue-navy active:shadow-active md:text-left md:justify-between md:pl-4 pseudo-shadow-hover`;
</script>

<svelte:head>
  {#if lat && lng}
    <link rel="preconnect" href={process.env.MAPBOX_API_BASE_URL} />
  {/if}
</svelte:head>

<div class="flex justify-center relative bg-blue-very-light" data-testid={dataTestId}>
  {#if lat && lng}
    <div class="absolute hidden md:block h-full top-0 right-0 bottom-0 left-0">
      <picture>
        <source media="(min-width: 1280px)" srcset={`${largeDefaultSrc}, ${largeHighResSrc} 2x`} />
        <source media="(min-width: 1024px)" srcset={`${mediumDefaultSrc}, ${mediumHighResSrc} 2x`} />
        <source media="(min-width: 768px)" srcset={`${smallDefaultSrc}, ${smallHighResSrc} 2x`} />
        <source
          media="(max-width: 767px)"
          srcset="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
        />
        <img
          class="h-full w-full object-cover object-right ml-auto mix-blend-luminosity"
          src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
          width="768"
          height="286"
          alt={mapAlt}
        />
      </picture>
      <div class="h-full absolute top-0 right-0 bottom-0 left-0"></div>
    </div>
  {/if}
  <div
    class="flex flex-col items-center relative w-full md:max-w-full lg:max-w-6xl"
    class:md:flex-row-reverse={(alignment === 'left') & !actionPromoTiles && !actionLinks}
    class:md:flex-row={alignment === 'right' && !actionPromoTiles && !actionLinks}
    class:lg:flex-row={(alignment === 'right' && actionPromoTiles) || actionLinks}
    class:lg:justify-center={alignment === 'center'}
  >
    <div
      class={`w-full lg:w-3/4 pt-4 md:pt-6 ${
        alignment === 'center' ? 'lg:pb-8 lg:pt-14 items-center' : 'lg:pb-4 lg:pt-8'
      } md:relative flex flex-col`}
    >
      {#if headingTextTemplate}
        <div class="px-4 sm:mb-2" class:mx-auto={alignment === 'center'}>
          {#if headingTextTemplate}
            <BaseHeading
              {headingLevel}
              className={`mb-2 text-blue-dark-navy text-1.5xl md:text-2xl ${alignment === 'center' ? 'text-center' : 'font-semibold md:text-left'} ${increaseHeadingSize || alignment === 'center' ? 'lg:text-4xl' : 'lg:text-3xl'}`
              }
              dataTestId="{dataTestId}-heading"
            >
              {@html headingTextTemplate}
            </BaseHeading>
          {/if}
          {#if contentTemplate}
            <div
              class={`text-gray-darkest leading-relaxed lg:text-lg ${alignment === 'center' ? 'text-center' : 'md:text-left'}`}
            >
              {@html contentTemplate}
            </div>
          {/if}
        </div>
      {/if}
      {#if searchComponent}
        <div class="w-full sm:ml-1 mb-6 max-w-152">
          {#if searchComponent.__typename === 'DrugSearch'}
            <DrugSearch {...searchComponent} {lang} parentComponent='mastheadSearchPromo'/>
          {:else if searchComponent.__typename === 'PharmacySearch'}
            <PharmacySearch {...searchComponent} {filters} centerAligned={alignment === 'center'} parentComponent='mastheadSearchPromo'/>
          {:else if searchComponent.__typename === 'ProviderSearch'}
            <ProviderSearch {...searchComponent} centerAligned={alignment === 'center'} {lang} />
          {/if}
        </div>
      {/if}
    </div>
    {#if backgroundImage && !lat && !lng & !actionPromoTiles && !actionLinks}
      <div
        class="hidden md:flex md:w-1/2 h-full p-4 md:py-10 items-center justify-center"
        class:lg:pl-1={alignment === 'left'}
        class:lg:pr-1={alignment === 'right'}
      >
        <img
          class="object-contain h-auto w-full max-w-md"
          src={backgroundImage.url}
          alt={backgroundImage.alt}
          width="187"
          height="135"
          srcset={`${backgroundImage.url} 1w, ${mdSrc} 404w, ${lgSrc} 448w`}
          sizes="(min-width: 768px) 404px, (min-width: 1024px) 448px"
        />
      </div>
    {/if}
    {#if actionPromoTiles && actionPromoTiles.length || actionLinks}
      <div
        class={`px-3 md:px-4 w-full mb-6 md:mb-8 lg:my-8 lg:max-w-106 grid gap-4 ${actionLinks && actionLinks.length > 0 ? 'max-w-94 md:max-w-none md:grid-cols-3 lg:grid-cols-1' : 'self-start sm:max-w-152 sm:ml-1 md:max-w-full lg:grid-cols-1 lg:my-11'}`}
        class:md:grid-cols-2={actionPromoTiles.length === 2}
      >
        {#if actionLinks && actionLinks.length > 0}
          {#each actionLinks as action, index (index)}
            <BaseLink
              className="relative flex items-center justify-center text-sm md:text-0.5sm text-center w-full px-4 py-3 md:px-2 shadow-medium font-semibold rounded bg-blue-cerulean  
              text-white  hover-hover:hover:bg-blue-darkish active:shadow-active md:text-left md:text-blue-navy md:bg-white md:justify-between md:pl-4 hover-hover:md:hover:bg-gray-mercury lg:min-h-12"
              url={action.url}
              target={action.target}
            >
              {action.text}
              <svg class="w-6 h-6 min-w-6 min-h-6 fill-blue-cerulean hidden md:block">
                <use xlink:href={`${SPRITESHEET_URL}#chevron-right`} />
              </svg>
            </BaseLink>
          {/each}
        {/if}
        {#if actionPromoTiles}
          {#each actionPromoTiles as actionPromoTile, index (index)}
            <BaseLink
              className={actionPromoTilesStyles}
              url={actionPromoTile.link.url}
              target={actionPromoTile.link.target}
              onClick={() => {
                if (actionPromoTile.pharmacy) window.umami.track('mastheadSearchPromo-pharmacy-coupon-click', { pharmacy: actionPromoTile.pharmacy })
              }}
            >
              {#if actionLinks && actionLinks.length > 0}
                {actionPromoTile.headingText}
              {:else}
                {#if actionPromoTile.icon && actionPromoTile.icon.url && actionPromoTile.icon.alt}
                  <img
                    class="w-16 h-16 min-w-16 min-h-16 mr-3"
                    class:-ml-2={actionPromoTile.pharmacy}
                    src={actionPromoTile.icon.url}
                    alt={actionPromoTile.icon.alt}
                  />
                {/if}
                <div class="text-left mr-2">
                  <BaseHeading
                    headingLevel={actionPromoTile.headingLevel || 'h2'}
                    className="mb-1 font-semibold text-base lg:text-lg">{actionPromoTile.headingText}</BaseHeading
                  >
                  {@html actionPromoTile.content}
                </div>
              {/if}
              <svg
                class="w-6 h-6 min-w-6 min-h-6 fill-blue-cerulean"
                class:hidden={actionPromoTile && actionPromoTile.length > 0}
                class:md:block={actionPromoTile && actionPromoTile.length > 0}
              >
                <use xlink:href={`${SPRITESHEET_URL}#chevron-right`} />
              </svg>
            </BaseLink>
          {/each}
        {/if}
      </div>
    {/if}
  </div>
</div>
